/**
 * PR-36A — Live Narrative Engine.
 * Transforms cold telemetry (xG, possession, pressure, momentum, fatigue)
 * into short emotional one-liners. Powers the Emotional Match Engine, the
 * cinematic timeline, and Community Sentiment narration.
 *
 * Design rules:
 * - Output is system-controlled, max 2 short lines.
 * - Throttled to one beat every 10–20s (micro-dopamine cadence).
 * - Never blocks the pitch — caller renders compact and auto-dismisses.
 * - Demo mode safe: pure functions, no side-effects beyond audit.
 */
import { trackEvent } from "@/lib/audit";
import { t, type Lang } from "@/lib/i18n";

export type NarrativeMoment =
  | "tension"
  | "dominio"
  | "caos"
  | "momentum"
  | "presion"
  | "peligro"
  | "recuperacion"
  | "agotamiento"
  | "desesperacion"
  | "control";

export type NarrativeKind =
  | "match_narrative"
  | "player_focus"
  | "community_sentiment"
  | "danger_alert"
  | "momentum_shift";

export interface NarrativeBeat {
  id: string;
  kind: NarrativeKind;
  moment: NarrativeMoment;
  emoji: string;
  text: string;
  intensity: number; // 0..1 — for UI weight / haptics
  ts: number;
}

export interface TelemetrySnapshot {
  minute: number;
  possessionHome: number;       // 0..1 (home share)
  pressureHome: number;         // 0..1
  pressureAway: number;         // 0..1
  momentum: number;             // -1..1 (positive = home)
  xgHomeRate: number;           // 0..1 expected goal in next minute
  xgAwayRate: number;           // 0..1
  fatigueHome: number;          // 0..1
  fatigueAway: number;          // 0..1
  starName?: string;
  starZone?: "left" | "right" | "center";
  homeLabel: string;
  awayLabel: string;
}

const MIN_GAP_MS = 10_000;
const MAX_GAP_MS = 20_000;

/**
 * FASE 2 — Live overlays i18n.
 * Narrative beats are template KEYS into the global dictionary so the
 * Ticker can render them in the user's active language at runtime.
 */
const DOMINIO = ["narrative_dominio_1", "narrative_dominio_2"];
const PRESION = ["narrative_presion_1", "narrative_presion_2"];
const PELIGRO = ["narrative_peligro_1", "narrative_peligro_2"];
const MOMENTUM = ["narrative_momentum_1", "narrative_momentum_2"];
const CAOS = ["narrative_caos_1", "narrative_caos_2"];
const RECUP = ["narrative_recup_1", "narrative_recup_2"];
const AGOT = ["narrative_agot_1", "narrative_agot_2"];
const DESESP = ["narrative_desesp_1", "narrative_desesp_2"];
const CONTROL = ["narrative_control_1", "narrative_control_2"];
const TENSION = ["narrative_tension_1", "narrative_tension_2"];
const PLAYER_FOCUS = [
  "narrative_player_1",
  "narrative_player_2",
  "narrative_player_3",
];

function pick(arr: string[], seed: number): string {
  return arr[Math.abs(seed) % arr.length];
}

function zoneLabel(lang: Lang, zone?: TelemetrySnapshot["starZone"]): string {
  if (zone === "right") return t("narrative_zone_right", lang);
  if (zone === "center") return t("narrative_zone_center", lang);
  return t("narrative_zone_left", lang);
}

/** Classify the dominant emotional moment from a telemetry snapshot. */
export function classifyMoment(s: TelemetrySnapshot): { moment: NarrativeMoment; team: "home" | "away"; intensity: number } {
  const homeBias = s.possessionHome - 0.5;
  const xgPeak = Math.max(s.xgHomeRate, s.xgAwayRate);
  const fatigueGap = Math.abs(s.fatigueHome - s.fatigueAway);
  const pressGap = s.pressureHome - s.pressureAway;
  const team: "home" | "away" = s.momentum >= 0 ? "home" : "away";

  if (xgPeak > 0.55) return { moment: "peligro", team: s.xgHomeRate > s.xgAwayRate ? "home" : "away", intensity: xgPeak };
  if (Math.abs(s.momentum) < 0.15 && (s.pressureHome + s.pressureAway) > 1.4) return { moment: "caos", team, intensity: 0.8 };
  if (Math.abs(homeBias) > 0.18) return { moment: "dominio", team: homeBias > 0 ? "home" : "away", intensity: Math.min(1, Math.abs(homeBias) * 3) };
  if (Math.abs(pressGap) > 0.25) return { moment: "presion", team: pressGap > 0 ? "away" : "home", intensity: Math.abs(pressGap) };
  if (Math.abs(s.momentum) > 0.45) return { moment: "momentum", team, intensity: Math.abs(s.momentum) };
  if (fatigueGap > 0.2) return { moment: "agotamiento", team: s.fatigueHome > s.fatigueAway ? "home" : "away", intensity: fatigueGap };
  if (s.minute >= 80 && Math.abs(s.momentum) > 0.2) return { moment: "desesperacion", team, intensity: 0.7 };
  if (s.minute >= 80) return { moment: "tension", team, intensity: 0.75 };
  if (Math.abs(homeBias) > 0.08) return { moment: "control", team: homeBias > 0 ? "home" : "away", intensity: 0.5 };
  return { moment: "recuperacion", team, intensity: 0.4 };
}

function templateFor(moment: NarrativeMoment): string[] {
  switch (moment) {
    case "dominio":      return DOMINIO;
    case "presion":      return PRESION;
    case "peligro":      return PELIGRO;
    case "momentum":     return MOMENTUM;
    case "caos":         return CAOS;
    case "recuperacion": return RECUP;
    case "agotamiento":  return AGOT;
    case "desesperacion":return DESESP;
    case "control":      return CONTROL;
    case "tension":      return TENSION;
  }
}

function emojiFor(moment: NarrativeMoment): string {
  return ({
    dominio: "🔥", presion: "⚠️", peligro: "🚨", momentum: "📈",
    caos: "💥", recuperacion: "🛡️", agotamiento: "😮‍💨",
    desesperacion: "😱", control: "🧠", tension: "⏳",
  } as const)[moment];
}

/** Stateful generator — keeps cadence + de-dupe. */
export class LiveNarrativeEngine {
  private lastEmit = 0;
  private nextGap = MIN_GAP_MS;
  private recentTexts: string[] = [];

  reset() { this.lastEmit = 0; this.recentTexts = []; this.nextGap = MIN_GAP_MS; }

  tick(snap: TelemetrySnapshot, now: number = Date.now(), lang: Lang = "ES"): NarrativeBeat | null {
    const since = now - this.lastEmit;
    if (since < this.nextGap) return null;

    const { moment, team, intensity } = classifyMoment(snap);
    const teamLabel = team === "home" ? snap.homeLabel : snap.awayLabel;
    const seed = snap.minute + Math.floor(now / 1000);

    // 25% chance to highlight a star player if available + moment is offensive
    const wantPlayer = !!snap.starName && (moment === "peligro" || moment === "dominio" || moment === "momentum");
    let kind: NarrativeKind;
    let text: string;
    let emoji: string;

    if (wantPlayer && (seed % 4 === 0)) {
      kind = "player_focus";
      emoji = "🧠";
      const key = pick(PLAYER_FOCUS, seed);
      text = t(key as Parameters<typeof t>[0], lang, {
        player: snap.starName!,
        zone: zoneLabel(lang, snap.starZone),
      });
    } else {
      kind = moment === "peligro" ? "danger_alert" : moment === "momentum" ? "momentum_shift" : "match_narrative";
      emoji = emojiFor(moment);
      const key = pick(templateFor(moment), seed);
      text = t(key as Parameters<typeof t>[0], lang, { team: teamLabel });
    }

    if (this.recentTexts.includes(text)) return null;
    this.recentTexts = [...this.recentTexts.slice(-4), text];
    this.lastEmit = now;
    this.nextGap = MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS);

    const beat: NarrativeBeat = {
      id: `nb-${now}`, kind, moment, emoji, text, intensity, ts: now,
    };
    void trackEvent("live_narrative_beat_emitted", { moment, kind, minute: snap.minute, intensity });
    return beat;
  }
}

/** Pure helper for community-sentiment surfaces. */
export function buildCommunitySentimentBeat(
  sentiment: { excitement: number; tension: number; frustration: number },
  seed = Date.now(),
  lang: Lang = "ES",
): NarrativeBeat {
  const dominant =
    sentiment.excitement > sentiment.tension && sentiment.excitement > sentiment.frustration ? 0
    : sentiment.tension >= sentiment.frustration ? 1 : 2;
  const key =
    dominant === 0 ? "narrative_community_excitement" :
    dominant === 1 ? "narrative_community_tension" :
    "narrative_community_frustration";
  const text = t(key, lang);
  const intensity = Math.max(sentiment.excitement, sentiment.tension, sentiment.frustration);
  void trackEvent("community_sentiment_beat_emitted", { dominant, intensity });
  return {
    id: `cs-${seed}`, kind: "community_sentiment",
    moment: dominant === 0 ? "dominio" : dominant === 1 ? "tension" : "presion",
    emoji: dominant === 0 ? "🔥" : dominant === 1 ? "😬" : "⚠️",
    text, intensity, ts: seed,
  };
}

export const NARRATIVE_MOMENTS: NarrativeMoment[] = [
  "tension", "dominio", "caos", "momentum", "presion",
  "peligro", "recuperacion", "agotamiento", "desesperacion", "control",
];

export const MICRO_DOPAMINE_CADENCE_MS = { min: MIN_GAP_MS, max: MAX_GAP_MS };